import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";

const STORAGE_KEY = "fod_cookie_consent";
const CONTENTSQUARE_SRC = "https://t.contentsquare.net/uxa/236ed269e05bf.js";
const GOOGLE_TAG_ID = "GT-TB7RKMHX";
const LINKEDIN_PARTNER_ID = "8655410";

function loadContentsquare() {
    if (typeof document === "undefined") return;
    if (document.querySelector(`script[src="${CONTENTSQUARE_SRC}"]`)) return;
    const s = document.createElement("script");
    s.src = CONTENTSQUARE_SRC;
    s.async = true;
    document.head.appendChild(s);
}

function loadGoogleTag() {
    if (typeof document === "undefined") return;
    const gtagSrc = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_TAG_ID}`;
    if (document.querySelector(`script[src="${gtagSrc}"]`)) return;

    const s = document.createElement("script");
    s.src = gtagSrc;
    s.async = true;
    document.head.appendChild(s);

    window.dataLayer = window.dataLayer || [];
    const gtag = function () { window.dataLayer.push(arguments); };
    window.gtag = gtag;
    gtag("js", new Date());
    gtag("config", GOOGLE_TAG_ID, { anonymize_ip: true });
}

function loadLinkedInInsight() {
    if (typeof document === "undefined") return;
    if (window._linkedin_data_partner_ids && window._linkedin_data_partner_ids.length > 0) return;

    window._linkedin_partner_id = LINKEDIN_PARTNER_ID;
    window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
    window._linkedin_data_partner_ids.push(LINKEDIN_PARTNER_ID);

    if (!window.lintrk) {
        window.lintrk = function (a, b) { window.lintrk.q.push([a, b]); };
        window.lintrk.q = [];
    }
    const s = document.createElement("script");
    s.type = "text/javascript";
    s.async = true;
    s.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
    const first = document.getElementsByTagName("script")[0];
    first.parentNode.insertBefore(s, first);
}

// Trackers only on the production domain (never on preview / localhost).
const TRACKING_HOST = "www.fodbydmb.com";

function loadAllTrackers() {
    if (typeof window === "undefined" || window.location.hostname !== TRACKING_HOST) return;
    try { loadContentsquare(); } catch (e) { console.error("Contentsquare load failed:", e); }
    try { loadGoogleTag(); } catch (e) { console.error("Google Tag load failed:", e); }
    try { loadLinkedInInsight(); } catch (e) { console.error("LinkedIn Insight load failed:", e); }
}

export default function CookieBanner() {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const v = localStorage.getItem(STORAGE_KEY);
        if (!v) {
            setVisible(true);
        } else if (v === "accepted") {
            loadAllTrackers();
        }
        const reopen = () => setVisible(true);
        window.addEventListener("fod-open-cookie-banner", reopen);
        return () => window.removeEventListener("fod-open-cookie-banner", reopen);
    }, []);

    const handle = (val) => {
        const previous = localStorage.getItem(STORAGE_KEY);
        if (val === "refused") {
            try { window.posthog?.opt_out_capturing?.(); window.posthog?.stopSessionRecording?.(); } catch (e) { /* noop */ }
            window.__fodPurgeTrackers?.();
        }
        localStorage.setItem(STORAGE_KEY, val);
        localStorage.setItem(`${STORAGE_KEY}_at`, new Date().toISOString());
        setVisible(false);
        window.dispatchEvent(new Event("fod-consent"));
        if (val === "accepted") {
            window.__fodEnablePosthog?.();
            loadAllTrackers();
        } else if (previous === "accepted") {
            window.location.reload();
        }
    };

    if (!visible) return null;

    return (
        <div
            data-testid="cookie-banner"
            role="dialog"
            aria-labelledby="cookie-title"
            className="fixed z-[9999] bg-white text-navy border-border-default animate-fade-up inset-x-0 bottom-0 border-t rounded-t-2xl shadow-[0_-12px_40px_-16px_rgba(15,27,45,0.35)] px-4 pt-3.5 pb-[max(14px,env(safe-area-inset-bottom))] md:inset-x-auto md:right-6 md:bottom-6 md:max-w-[440px] md:border md:rounded-2xl md:shadow-[0_24px_60px_-20px_rgba(15,27,45,0.45)] md:p-6"
        >
            <button
                type="button"
                onClick={() => handle("refused")}
                aria-label="Fermer"
                data-testid="cookie-close-btn"
                className="hidden md:flex absolute right-3 top-3 w-9 h-9 items-center justify-center rounded-md text-grey-text-soft hover:text-navy hover:bg-sand transition-colors"
            >
                <X size={18} />
            </button>
            <h3 id="cookie-title" className="hidden md:block m-0 mb-2 pr-8 text-lg font-bold">Cookies &amp; analyses d'audience</h3>
            <p data-testid="cookie-text-mobile" className="md:hidden m-0 text-[13px] leading-snug text-grey-text">
                <span className="sr-only">Cookies &amp; analyses d'audience. </span>
                Nous mesurons l'audience (Google Analytics, Contentsquare, LinkedIn) uniquement avec votre accord.{" "}
                <Link to="/politique-de-confidentialite" className="text-navy font-medium underline underline-offset-2">En savoir plus</Link>
            </p>
            <p className="hidden md:block m-0 text-sm leading-relaxed text-grey-text">
                Nous utilisons des cookies et les outils d'analyse <strong className="text-navy">Google Analytics</strong>,{" "}
                <strong className="text-navy">Contentsquare</strong> et <strong className="text-navy">LinkedIn Insight Tag</strong>{" "}
                (heatmaps, session replay, mesure d'audience) pour comprendre comment vous interagissez avec notre site et
                améliorer votre expérience. Aucun cookie de pistage n'est déposé tant que vous n'avez pas accepté.{" "}
                <Link to="/politique-de-confidentialite" className="text-navy underline underline-offset-2 hover:text-gold-text">
                    En savoir plus
                </Link>
                .
            </p>
            <div className="mt-3 md:mt-4 flex gap-2">
                <button
                    type="button"
                    data-testid="cookie-refuse-btn"
                    onClick={() => handle("refused")}
                    className="flex-1 min-h-[44px] px-4 border-[1.5px] border-border-strong rounded-lg bg-white text-navy font-semibold text-sm hover:border-navy transition-colors"
                >
                    Refuser
                </button>
                <button
                    type="button"
                    data-testid="cookie-accept-btn"
                    onClick={() => handle("accepted")}
                    className="flex-1 min-h-[44px] px-4 rounded-lg bg-navy text-white font-semibold text-sm hover:bg-navy-band transition-colors"
                >
                    Accepter
                </button>
            </div>
        </div>
    );
}
